/**
 * True only for http(s) URLs. Rejects javascript:, data:, vbscript:, blob:,
 * file:, etc., so a poisoned feed value can't become an XSS vector when used
 * as an href (or navigated to).
 */
export function isSafeHttpUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const base = typeof document !== 'undefined' ? document.baseURI : 'https://localhost';
    const u = new URL(url, base);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Returns `url` only if it's a safe http(s) URL, otherwise undefined — so a bad
 *  value never lands in an `href` as a javascript:/data: URL. */
export function safeUrl(url: string | undefined): string | undefined {
  return isSafeHttpUrl(url) ? url : undefined;
}

/** Minimal shape of a (React or native) click event — avoids a React import. */
interface ClickLike {
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  button: number;
  preventDefault: () => void;
}

/**
 * Navigate to `url` in the working (current) tab on a plain left click.
 *
 * The widget embeds on botilia.gr, an Angular SPA whose capture-phase document
 * click handler intercepts same-origin `<a>` clicks for client-side routing and
 * cancels the native navigation — so a plain `target="_self"` link is unreliable
 * there. A programmatic `window.location` assignment can't be intercepted by a
 * link-click handler, so the click reliably opens in the working tab.
 *
 * Modified/middle clicks (cmd/ctrl/shift/alt/middle) are left to the browser so
 * "open in new tab/window" still works via the anchor's href. Only http(s) URLs
 * are navigated to — a poisoned `javascript:`/`data:` feed value is blocked.
 */
export function openInWorkingTab(url: string | undefined, e: ClickLike): void {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
  e.preventDefault();
  if (isSafeHttpUrl(url)) {
    window.location.assign(url as string);
  }
}
